// Radioisotopes/Test Kits Section Component - Enhanced with encrypted query parameter support for orgId and appId
// Now retrieves orgId and appId from URL query parameters (encrypted) with fallback to URL params
// No localStorage fallback - parameters must be provided via URL
import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  InputLabel,
  RadioGroup,
  Radio
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import LaboratoryService from '../../../services/laboratoryService';
import { parseEncryptedQuery, decryptText, decryptAppId } from '../../../utils/encryption';

const RadioisotopeSection = forwardRef((props, ref) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { orgId: urlOrgId } = useParams();
  
  // Function to get orgId from URL query parameters with decryption
  const getOrgIdFromQuery = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get('orgId');
    if (encryptedOrgId) {
      try {
        const decryptedOrgId = decryptText(encryptedOrgId);
        console.log('📍 RadioisotopeSection - Decrypted orgId from URL query:', decryptedOrgId);
        console.log('📍 RadioisotopeSection - Original encrypted orgId:', encryptedOrgId);
        return decryptedOrgId;
      } catch (error) {
        console.log('⚠️ RadioisotopeSection - Failed to decrypt orgId, using original:', encryptedOrgId, error);
        return encryptedOrgId; // Return original if decryption fails
      }
    }
    return null;
  }, [location.search]);

  // Function to get appId from URL query parameters with decryption
  const getAppIdFromQuery = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get('appId');
    if (encryptedAppId) {
      try {
        const decryptedAppId = decryptAppId(encryptedAppId);
        console.log('📍 RadioisotopeSection - Decrypted appId from URL query:', decryptedAppId);
        console.log('📍 RadioisotopeSection - Original encrypted appId:', encryptedAppId);
        return decryptedAppId;
      } catch (error) {
        console.log('⚠️ RadioisotopeSection - Failed to decrypt appId, using original:', encryptedAppId, error);
        return encryptedAppId; // Return original if decryption fails
      }
    }
    return null;
  }, [location.search]);

  // Function to get orgId with priority: URL query (encrypted) > URL params > null
  const getOrgId = useCallback(() => {
    // First check URL query parameters (encrypted) - highest priority
    const queryOrgId = getOrgIdFromQuery();
    if (queryOrgId) {
      console.log('📍 RadioisotopeSection - Using decrypted orgId from URL query:', queryOrgId);
      return queryOrgId;
    }

    // Then check URL parameters (unencrypted)
    if (urlOrgId) {
      console.log('📍 RadioisotopeSection - Using orgId from URL params:', urlOrgId);
      return urlOrgId;
    }

    // No fallback - orgId must be provided via URL
    console.log('⚠️ RadioisotopeSection - No orgId found in URL parameters');
    return null;
  }, [getOrgIdFromQuery, urlOrgId]);

  // Function to get appId with priority: URL query (encrypted) only
  const getAppId = useCallback(() => {
    // Only check URL query parameters (encrypted)
    const queryAppId = getAppIdFromQuery();
    if (queryAppId) {
      console.log('📍 RadioisotopeSection - Using decrypted appId from URL query:', queryAppId);
      return queryAppId;
    }

    // No fallback - appId must be provided via URL query parameters
    console.log('⚠️ RadioisotopeSection - No appId found in URL query parameters');
    return null;
  }, [getAppIdFromQuery]);

  // State
  const [radioisotopes, setRadioisotopes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingRadioisotope, setEditingRadioisotope] = useState(null);
  const [viewingRadioisotope, setViewingRadioisotope] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [disciplines, setDisciplines] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    // Check URL parameter to determine initial tab
    const tabParam = searchParams.get('tab');
    if (tabParam === 'view' || tabParam === '1') return 1;
    if (tabParam === 'add' || tabParam === '0') return 0;
    return 0;
  });
  const [radioisotopeMasterData, setRadioisotopeMasterData] = useState([]);
  const [typeOfTestOptions, setTypeOfTestOptions] = useState([]);
  
  // Get form data from encrypted query parameters and URL parameters
  const [formData] = useState(() => {
    const { disciplineId, disciplineName } = parseEncryptedQuery(searchParams);
    return {
      disciplineId: disciplineId || searchParams.get('disciplineId'),
      disciplineName: disciplineName || searchParams.get('disciplineName'),
      appId: getAppId(),
      orgId: getOrgId(),
      userId: localStorage.getItem('userId')
    };
  });

  // Form state
  const [radioisotopeData, setRadioisotopeData] = useState({
    typeOfTest: [], // Changed to array for multiple selection
    nameofRadioisotopes: '',
    halfLife: '',
    energyKeV: '',
    maxactivityPermitted: '',
    detailsofSupplier: '',
    supportingEntity: ''
  });

  // Load data on component mount
  useEffect(() => {
    // Load radioisotopes and disciplines when component mounts
    loadRadioisotopes();
    loadDisciplines();
    loadRadioisotopeMasterData();
    loadTypeOfTestOptions();
  }, []);

  // Listen for URL parameter changes to switch tabs
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'view' || tabParam === '1') {
      setActiveTab(1);
    } else if (tabParam === 'add' || tabParam === '0') {
      setActiveTab(0);
    }
  }, [searchParams]);

  // Load radioisotopes from API
  const loadRadioisotopes = async () => {
    try {
      setLoading(true);
      const response = await LaboratoryService.getRadioisotopes(
        formData.appId,
        formData.orgId,
        formData.disciplineId
      );
      setRadioisotopes(response.data || []);
    } catch (error) {
      setError('Failed to load radioisotopes: ' + error.message);
      setRadioisotopes([]);
    } finally {
      setLoading(false);
    }
  };

  // Load disciplines for dropdown
  const loadDisciplines = async () => {
    try {
      const response = await LaboratoryService.getDisciplines();
      setDisciplines(response.data || []);
    } catch (error) {
      console.error('Failed to load disciplines:', error);
    }
  };

  // Load radioisotope master data from Radioisotopes table
  const loadRadioisotopeMasterData = async () => {
    try {
      const response = await LaboratoryService.getRadioisotopeMasterData();
      setRadioisotopeMasterData(response.data || []);
    } catch (error) {
      console.error('Failed to load radioisotope master data:', error);
      setRadioisotopeMasterData([]);
    }
  };

  // Load type of test options from form153Scope table
  const loadTypeOfTestOptions = async () => {
    try {
      
      // Debug localStorage values
      const { disciplineId: decryptedDisciplineId } = parseEncryptedQuery(searchParams);
      console.log('🔍 RadioisotopeSection - Debug parameter values:', {
        appId: getAppId(),
        orgId: getOrgId(),
        disciplineId: decryptedDisciplineId,
        userId: localStorage.getItem('userId')
      });
      
      if (!formData.appId || !formData.orgId || !formData.disciplineId) {
        console.warn('Missing required data for loading type of test options:', {
          appId: formData.appId,
          orgId: formData.orgId,
          disciplineId: formData.disciplineId
        });
        setTypeOfTestOptions([]);
        return;
      }
      
      console.log('Calling API with:', {
        appId: formData.appId,
        orgId: formData.orgId,
        disciplineId: formData.disciplineId
      });
      
      const response = await LaboratoryService.getTypeOfTestOptions(
        formData.appId,
        formData.orgId,
        formData.disciplineId
      );
      
      console.log('Type of test options response:', response);
      setTypeOfTestOptions(response.data || []);
    } catch (error) {
      console.error('Failed to load type of test options:', error);
      setTypeOfTestOptions([]);
    }
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setRadioisotopeData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle checkbox changes for type of test
  const handleTypeOfTestChange = (testId, checked) => {
    setRadioisotopeData(prev => ({
      ...prev,
      typeOfTest: checked 
        ? [...prev.typeOfTest, testId]
        : prev.typeOfTest.filter(id => id !== testId)
    }));
  };

  // Handle radioisotope selection change
  const handleRadioisotopeSelectionChange = (selectedRadioisotope) => {
    if (selectedRadioisotope) {
      const masterData = radioisotopeMasterData.find(item => item.Name === selectedRadioisotope);
      if (masterData) {
        setRadioisotopeData(prev => ({
          ...prev,
          nameofRadioisotopes: selectedRadioisotope,
          halfLife: masterData.HalfLife || '',
          energyKeV: masterData.Energy || ''
        }));
      }
    }
  };

  // Function to open View Existing tab (can be called from parent components)
  const openViewExistingTab = () => {
    setActiveTab(1);
  };

  // Function to open Add Radioisotope tab (can be called from parent components)
  const openAddRadioisotopeTab = () => {
    setActiveTab(0);
  };

  // Expose functions to parent component
  useImperativeHandle(ref, () => ({
    openViewExistingTab,
    openAddRadioisotopeTab,
    setActiveTab
  }));

  // Open add tab
  const handleAddRadioisotope = () => {
    setEditingRadioisotope(null);
    setRadioisotopeData({
      typeOfTest: [],
      nameofRadioisotopes: '',
      halfLife: '',
      energyKeV: '',
      maxactivityPermitted: '',
      detailsofSupplier: '',
      supportingEntity: ''
    });
    setActiveTab(0); // Switch to Add Radioisotope tab
  };

  // Open edit tab
  const handleEditRadioisotope = (radioisotope) => {
    setEditingRadioisotope(radioisotope);
    setRadioisotopeData({
      typeOfTest: radioisotope.TypeOfTest ? radioisotope.TypeOfTest.split(',').map(id => parseInt(id)) : [],
      nameofRadioisotopes: radioisotope.NameofRadioisotopes || '',
      halfLife: radioisotope.HalfLife || '',
      energyKeV: radioisotope.EnergyKeV || '',
      maxactivityPermitted: radioisotope.MaxactivityPermitted || '',
      detailsofSupplier: radioisotope.DetailsofSupplier || '',
      supportingEntity: radioisotope.supportingEntity || ''
    });
    setActiveTab(0); // Switch to Add Radioisotope tab for editing
  };

  // Open view dialog
  const handleViewRadioisotope = (radioisotope) => {
    setViewingRadioisotope(radioisotope);
    setViewDialogOpen(true);
  };

  // Save radioisotope (add or update)
  const handleSaveRadioisotope = async () => {
    try {
      if (!radioisotopeData.nameofRadioisotopes) {
        setError('Please select a radioisotope');
        return;
      }

      if (radioisotopeData.typeOfTest.length === 0) {
        setError('Please select at least one type of test');
        return;
      }

      const payload = {
        appId: parseInt(formData.appId),
        orgId: parseInt(formData.orgId),
        labLocationId: null, // Can be added if needed
        disciplineId: parseInt(formData.disciplineId),
        groupId: null, // Can be added if needed
        typeOfTest: radioisotopeData.typeOfTest.join(','),
        nameofRadioisotopes: radioisotopeData.nameofRadioisotopes,
        halfLife: radioisotopeData.halfLife,
        energyKeV: radioisotopeData.energyKeV,
        maxactivityPermitted: radioisotopeData.maxactivityPermitted,
        detailsofSupplier: radioisotopeData.detailsofSupplier,
        createdById: parseInt(formData.userId),
        updatedById: parseInt(formData.userId),
        supportingEntity: radioisotopeData.supportingEntity || '',
        clientIp: '127.0.0.1' // Should be dynamic in production
      };

      let response;
      if (editingRadioisotope) {
        response = await LaboratoryService.updateRadioisotope(editingRadioisotope.Id, payload);
        setSuccess('Radioisotope updated successfully');
      } else {
        response = await LaboratoryService.addRadioisotope(payload);
        setSuccess('Radioisotope added successfully');
      }

      // Reset form
      setRadioisotopeData({
        typeOfTest: [],
        nameofRadioisotopes: '',
        halfLife: '',
        energyKeV: '',
        maxactivityPermitted: '',
        detailsofSupplier: '',
        supportingEntity: ''
      });
      setEditingRadioisotope(null);

      // Reload radioisotopes
      await loadRadioisotopes();

      // Switch to view tab
      setActiveTab(1);

    } catch (error) {
      setError(error.message || 'Failed to save radioisotope');
    }
  };

  // Delete radioisotope
  const handleDeleteRadioisotope = async (radioisotopeId) => {
    try {
      if (window.confirm('Are you sure you want to delete this radioisotope?')) {
        await LaboratoryService.deleteRadioisotope(radioisotopeId, formData.userId, '127.0.0.1');
        setSuccess('Radioisotope deleted successfully');
        await loadRadioisotopes();
      }
    } catch (error) {
      setError(error.message || 'Failed to delete radioisotope');
    }
  };

  // Close view dialog
  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewingRadioisotope(null);
  };

  // Get discipline name by ID
  const getDisciplineName = (disciplineId) => {
    console.log('Getting discipline name for ID:', disciplineId, 'Type:', typeof disciplineId);
    console.log('Available disciplines:', disciplines);
    
    if (!disciplineId || disciplines.length === 0) return 'N/A';
    
    // Handle both string and number comparisons safely
    const discipline = disciplines.find(d => {
      if (!d || d.id === undefined || d.id === null) return false;
      
      return (
        d.id === disciplineId ||
        d.id === parseInt(disciplineId) ||
        (d.id !== null && d.id !== undefined ? d.id.toString() : '') === (disciplineId !== null && disciplineId !== undefined ? disciplineId.toString() : '')
      );
    });
    
    console.log('Found discipline:', discipline);
    return discipline ? discipline.disciplineName : `Unknown (ID: ${disciplineId})`;
  };

  // Format type of test display
  const formatTypeOfTest = (typeOfTestString, typeOfTestNames = null) => {
    console.log('Formatting type of test:', typeOfTestString);
    console.log('Type of test names from backend:', typeOfTestNames);
    
    // If we have the formatted names from the backend, use them directly
    if (typeOfTestNames) {
      return typeOfTestNames;
    }
    
    // Fallback to the old method if TypeOfTestNames is not available
    if (!typeOfTestString) return 'N/A';
    
    const testIds = typeOfTestString.split(',').map(id => parseInt(id.trim()));
    const testNames = testIds.map(id => {
      const option = typeOfTestOptions.find(opt => (opt.id || opt.Id) === id);
      return option ? option.TypeOfTest : `ID: ${id}`;
    });
    
    return testNames.join(', ');
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 1400, mx: "auto", p: 3 }}>
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          mb: 3,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            py: 2,
            px: 2,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            borderRadius: 1,
            mb: 0,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            🧪 Radioisotopes/Test Kits Management
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Display current discipline info */}
          {formData.disciplineName && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Current Discipline: {formData.disciplineName}
              </Typography>
            </Alert>
          )}

          {/* Tabs for different sections */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              mb: 3,
              "& .MuiTab-root": {
                minHeight: 48,
                fontSize: "0.875rem",
                fontWeight: 500,
                textTransform: "none",
                px: 3,
              },
            }}
          >
            <Tab
              label={
                editingRadioisotope ? "Edit Radioisotope" : "Add Radioisotope"
              }
              sx={{
                color: activeTab === 0 ? "#667eea" : "text.secondary",
                "&.Mui-selected": { color: "#667eea" },
              }}
            />
            <Tab
              label="View Existing Radioisotopes"
              sx={{
                color: activeTab === 1 ? "#667eea" : "text.secondary",
                "&.Mui-selected": { color: "#667eea" },
              }}
            />
          </Tabs>

          {/* Tab Content */}
          {activeTab === 0 && (
            <Box>
              <Typography
                variant="h6"
                sx={{ mb: 3, color: "#333", fontWeight: 600 }}
              >
                {editingRadioisotope
                  ? "Edit Radioisotope/Test Kit"
                  : "Add New Radioisotope/Test Kit"}
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel
                      component="legend"
                      sx={{ mb: 2, color: "#333", fontWeight: 500 }}
                    >
                      Type of Test *
                    </FormLabel>
                    <FormGroup>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {typeOfTestOptions.map((option) => (
                          <FormControlLabel
                            key={option.id || option.Id}
                            control={
                              <Checkbox
                                checked={radioisotopeData.typeOfTest.includes(
                                  parseInt(option.id || option.Id),
                                )}
                                onChange={(e) =>
                                  handleTypeOfTestChange(
                                    parseInt(option.id || option.Id),
                                    e.target.checked,
                                  )
                                }
                                size="small"
                              />
                            }
                            label={option.TypeOfTest}
                            sx={{
                              minWidth: "200px",
                              "& .MuiFormControlLabel-label": {
                                fontSize: "0.875rem",
                              },
                            }}
                          />
                        ))}
                      </Box>
                      {typeOfTestOptions.length === 0 && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ mt: 1 }}
                        >
                          No type of test options available for this discipline.
                        </Typography>
                      )}
                    </FormGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>
                      Name of Radioisotopes/Reference Materials/Test Kits *
                    </InputLabel>
                    <Select
                      value={radioisotopeData.nameofRadioisotopes}
                      onChange={(e) => {
                        handleInputChange(
                          "nameofRadioisotopes",
                          e.target.value,
                        );
                        handleRadioisotopeSelectionChange(e.target.value);
                      }}
                      label="Name of Radioisotopes/Reference Materials/Test Kits *"
                    >
                      {radioisotopeMasterData.map((item) => (
                        <MenuItem key={item.id} value={item.Name}>
                          {item.Name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Half Life *"
                    value={radioisotopeData.halfLife}
                    onChange={(e) =>
                      handleInputChange("halfLife", e.target.value)
                    }
                    size="small"
                    placeholder="Half Life"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Energy (KeV) *"
                    value={radioisotopeData.energyKeV}
                    onChange={(e) =>
                      handleInputChange("energyKeV", e.target.value)
                    }
                    size="small"
                    placeholder="Energy (KeV)"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Maximum Activity Permitted by AERB *"
                    value={radioisotopeData.maxactivityPermitted}
                    onChange={(e) =>
                      handleInputChange("maxactivityPermitted", e.target.value)
                    }
                    size="small"
                    placeholder="Maximum Activity Permitted by AERB"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Details of Supplier *"
                    value={radioisotopeData.detailsofSupplier}
                    onChange={(e) =>
                      handleInputChange("detailsofSupplier", e.target.value)
                    }
                    size="small"
                    multiline
                    rows={3}
                    placeholder="Details of Supplier"
                  />
                </Grid>
              </Grid>

              {/* Indicate supporting entity employment */}
              <Grid item xs={12}>
                <FormControl component="fieldset" sx={{ mt: 2 }}>
                  <FormLabel component="legend" sx={{ fontWeight: 400, mb: 1 }}>
                    Does this radioisotopes belong to an operational entity or a supporting entity?*
                  </FormLabel>
                  <RadioGroup
                    row
                    value={radioisotopeData.supportingEntity}
                    onChange={(e) =>
                      handleInputChange("supportingEntity", e.target.value)
                    }
                  >
                   <FormControlLabel value="Operational" control={<Radio />} label="Operational" />
                   <FormControlLabel value="Supporting" control={<Radio />} label="Supporting" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Box
                sx={{
                  mt: 4,
                  display: "flex",
                  gap: 2,
                  justifyContent: "center",
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleSaveRadioisotope}
                  sx={{
                    px: 4,
                    py: 1,
                    backgroundColor: "#667eea",
                    "&:hover": { backgroundColor: "#5a67d8" },
                  }}
                >
                  {editingRadioisotope
                    ? "Update Radioisotope"
                    : "Save Radioisotope"}
                </Button>
                {editingRadioisotope && (
                  <Button
                    variant="outlined"
                    onClick={handleAddRadioisotope}
                    sx={{ px: 4, py: 1 }}
                  >
                    Cancel Edit
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "#333", fontWeight: 600 }}
                >
                  Existing Radioisotopes/Test Kits
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddRadioisotope}
                  sx={{
                    backgroundColor: "#667eea",
                    "&:hover": { backgroundColor: "#5a67d8" },
                  }}
                >
                  Add Radioisotope
                </Button>
              </Box>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell sx={{ fontWeight: 600 }}>S.No.</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Name of Radioisotopes
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Type of Test
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Half Life
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Energy (KeV)
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Max Activity Permitted
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Details of Supplier
                        </TableCell>
                         <TableCell sx={{ fontWeight: 600 }}>
                          Entity Type
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600, textAlign: "center" }}
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {radioisotopes.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            sx={{ textAlign: "center", py: 4 }}
                          >
                            <Typography variant="body1" color="textSecondary">
                              No radioisotopes found. Click "Add Radioisotope"
                              to get started.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        radioisotopes.map((radioisotope, index) => (
                          <TableRow
                            key={radioisotope.Id}
                            sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}
                          >
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, minWidth: 120 }}
                              >
                                {radioisotope.NameofRadioisotopes}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ maxWidth: 200, wordBreak: "break-word" }}
                              >
                                {formatTypeOfTest(
                                  radioisotope.TypeOfTest,
                                  radioisotope.TypeOfTestNames,
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {radioisotope.HalfLife || "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {radioisotope.EnergyKeV || "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ maxWidth: 150, wordBreak: "break-word" }}
                              >
                                {radioisotope.MaxactivityPermitted || "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ maxWidth: 200, wordBreak: "break-word" }}
                              >
                                {radioisotope.DetailsofSupplier || "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ maxWidth: 200, wordBreak: "break-word" }}
                              >
                                {radioisotope.supportingEntity || "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  justifyContent: "center",
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleViewRadioisotope(radioisotope)
                                  }
                                  sx={{ color: "#1976d2" }}
                                  title="View Details"
                                >
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleEditRadioisotope(radioisotope)
                                  }
                                  sx={{ color: "#ed6c02" }}
                                  title="Edit"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleDeleteRadioisotope(radioisotope.Id)
                                  }
                                  sx={{ color: "#d32f2f" }}
                                  title="Delete"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Radioisotope Details</Typography>
          <IconButton onClick={handleCloseViewDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {viewingRadioisotope && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Name of Radioisotope
                </Typography>
                <Typography variant="body1">
                  {viewingRadioisotope.NameofRadioisotopes}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Type of Test
                </Typography>
                <Typography variant="body1">
                  {formatTypeOfTest(
                    viewingRadioisotope.TypeOfTest,
                    viewingRadioisotope.TypeOfTestNames,
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Half Life
                </Typography>
                <Typography variant="body1">
                  {viewingRadioisotope.HalfLife || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Energy (KeV)
                </Typography>
                <Typography variant="body1">
                  {viewingRadioisotope.EnergyKeV || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Maximum Activity Permitted
                </Typography>
                <Typography variant="body1">
                  {viewingRadioisotope.MaxactivityPermitted || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Details of Supplier
                </Typography>
                <Typography variant="body1">
                  {viewingRadioisotope.DetailsofSupplier || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Insert Date
                </Typography>
                <Typography variant="body1">
                  {viewingRadioisotope.InsertDate
                    ? new Date(viewingRadioisotope.InsertDate).toLocaleString()
                    : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Update Date
                </Typography>
                <Typography variant="body1">
                  {viewingRadioisotope.UpdateDate
                    ? new Date(viewingRadioisotope.UpdateDate).toLocaleString()
                    : "N/A"}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseViewDialog} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccess("")}
          severity="success"
          sx={{ width: "100%" }}
        >
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setError("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default RadioisotopeSection;